/**
 * Tramontina Mobile In-Grid Tag Filters & Top Active Pills
 * Operates strictly on Collection Piece Guide tag URLs (custom.piece_guide_cards)
 * matching sections/collection-piece-guide.liquid:
 * 1. Document-level event delegation prevents full-page reload on filter clicks
 * 2. CPG tag pills (/collections/<handle>/<tag>) and active removal pills
 * 3. Instant AJAX page transition with pushState URL updates
 * 4. Syncs product grid, toolbar, drawer counts, and piece guide section
 * 5. Dynamic sticky positioning aligned flush with header
 */
(function() {
  'use strict';

  function getStickyHeaderBottom() {
    if (typeof window.getTramontinaStickyHeaderBottom === 'function') {
      return window.getTramontinaStickyHeaderBottom();
    }

    let bottom = 0;
    const announcement = document.querySelector('.cc-announcement--sticky, [class*="announcement--sticky"]');
    if (announcement) {
      const aStyle = window.getComputedStyle(announcement);
      if ((aStyle.position === 'sticky' || aStyle.position === 'fixed') && aStyle.display !== 'none' && aStyle.visibility !== 'hidden') {
        const aRect = announcement.getBoundingClientRect();
        if (aRect.bottom > 0 && aRect.top <= 2) {
          bottom = Math.max(bottom, aRect.bottom);
        }
      }
    }

    const header = document.querySelector('store-header') || document.querySelector('.cc-header--sticky');
    if (header) {
      const hStyle = window.getComputedStyle(header);
      const isSticky = hStyle.position === 'sticky' || hStyle.position === 'fixed' ||
                       header.classList.contains('cc-header--sticky') ||
                       header.dataset.isSticky === 'true' ||
                       header.closest('.cc-header--sticky');
      if (isSticky && hStyle.display !== 'none' && hStyle.visibility !== 'hidden') {
        const hRect = header.getBoundingClientRect();
        if (hRect.bottom > 0 && hRect.top <= (bottom + 4)) {
          bottom = Math.max(bottom, hRect.bottom);
        }
      }
    }

    return Math.max(0, Math.round(bottom));
  }

  /**
   * Sets up dynamic sticky position & .is-stuck class for mobile active pills bar
   */
  function setupStickyActivePills() {
    const updatePillsStickyOffset = () => {
      const pillsBar = document.getElementById('tram-mobile-active-pills-bar');
      if (!pillsBar) return;

      const toolbar = document.getElementById('products-toolbar');
      const finalHeaderTop = getStickyHeaderBottom();
      document.documentElement.style.setProperty('--tram-toolbar-sticky-top', `${finalHeaderTop}px`);

      const isMobile = window.innerWidth < 900;
      let tbHeight = 0;
      if (toolbar && isMobile) {
        const isTbSticky = toolbar.classList.contains('is-sticky-mobile') || toolbar.dataset.stickMobile === 'true';
        if (isTbSticky) {
          tbHeight = toolbar.offsetHeight || 50;
        }
      }

      const pillsStickyTop = finalHeaderTop + tbHeight;
      document.documentElement.style.setProperty('--tram-active-pills-sticky-top', `${pillsStickyTop}px`);

      const pRect = pillsBar.getBoundingClientRect();
      const isPillsStuck = pRect.top <= (pillsStickyTop + 2);
      pillsBar.classList.toggle('is-stuck', isPillsStuck);
    };

    updatePillsStickyOffset();
    if (!window._hasTramPillsStickyScroll) {
      window._hasTramPillsStickyScroll = true;
      window.addEventListener('scroll', updatePillsStickyOffset, { passive: true });
      window.addEventListener('resize', updatePillsStickyOffset, { passive: true });
    }
  }

  /**
   * Document-Level Event Delegation for All Filter Pills & Drawer Triggers
   * Completely avoids missing listeners on re-rendered DOM elements and stops page reloads.
   */
  function bindGlobalFilterDelegation() {
    if (window._hasTramGlobalFilterDelegation) return;
    window._hasTramGlobalFilterDelegation = true;

    document.addEventListener('click', function(e) {
      // 1. Any CPG tag pill or active filter pill
      const pill = e.target.closest(
        '.js-cpg-tag-pill, .js-cpg-remove-pill, .tram-in-grid-filter-pill, .tram-mobile-active-pill, .tram-mobile-active-pill-clear-all'
      );
      if (pill) {
        let targetUrl = pill.getAttribute('data-target-url') || pill.getAttribute('href');
        if (targetUrl) {
          if (targetUrl.startsWith('http') && !targetUrl.includes(window.location.hostname)) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();

          if (pill.classList.contains('js-cpg-tag-pill') || pill.classList.contains('tram-in-grid-filter-pill')) {
            const filterParam = pill.getAttribute('data-filter-param');
            const filterVal = pill.getAttribute('data-filter-value');
            const tag = pill.getAttribute('data-tag');
            const isCurrentlyActive = pill.classList.contains('is-active') || pill.getAttribute('data-active') === 'true';

            try {
              const currentUrl = new URL(window.location.href);
              const currentParams = currentUrl.searchParams;

              // 1. Gather all filter param names used by any card in the top / in-grid filter
              const allTopFilterParams = new Set();
              document.querySelectorAll('.cpg-section .js-cpg-card, .js-cpg-tag-pill, .tram-in-grid-filter-pill').forEach(c => {
                const p = c.getAttribute('data-filter-param');
                if (p) allTopFilterParams.add(p);
              });

              // 2. Delete ALL top filter parameters (removes any old filter selected in top filter)
              allTopFilterParams.forEach(p => {
                currentParams.delete(p);
              });

              // 3. Normalize pathname to root collection handle
              let rootPath = currentUrl.pathname;
              const pathParts = rootPath.split('/').filter(Boolean);
              if (pathParts.length >= 3 && pathParts[0] === 'collections') {
                rootPath = '/collections/' + pathParts[1];
              }

              if (isCurrentlyActive) {
                const qs = currentParams.toString();
                targetUrl = rootPath + (qs ? '?' + qs : '');
              } else if (filterParam && filterVal) {
                currentParams.append(filterParam, filterVal);
                const qs = currentParams.toString();
                targetUrl = rootPath + (qs ? '?' + qs : '');
              } else if (tag) {
                const qs = currentParams.toString();
                targetUrl = rootPath + '/' + encodeURIComponent(tag) + (qs ? '?' + qs : '');
              }
            } catch(err) {
              console.warn('Filter URL error:', err);
            }
          }

          navigateToTagUrl(targetUrl);
          return;
        }
      }

      // 2. "More Filters" button inside in-grid filter card header
      const moreBtn = e.target.closest('.js-open-tram-drawer-tab');
      if (moreBtn) {
        e.preventDefault();
        e.stopPropagation();
        const tabName = (moreBtn.getAttribute('data-filter-name') || '').toLowerCase().trim();
        const facetFilters = document.querySelector('facet-filters');

        if (facetFilters && typeof facetFilters.open === 'function') {
          facetFilters.open(moreBtn);

          if (tabName) {
            setTimeout(() => {
              const accordions = facetFilters.querySelectorAll('details.tram-filter-accordion');
              for (const acc of accordions) {
                const title = acc.querySelector('.tram-accordion-title');
                if (title && title.textContent.toLowerCase().includes(tabName)) {
                  acc.open = true;
                  acc.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  break;
                }
              }
            }, 180);
          }
        }
      }
    });
  }

  /**
   * Performs seamless AJAX tag navigation matching collection-piece-guide.liquid
   */
  async function navigateToTagUrl(targetUrl) {
    if (!targetUrl) return;

    const resultsEl = document.getElementById('filter-results') || document.querySelector('.main-products-grid__results');
    const toolbarEl = document.getElementById('products-toolbar');

    if (resultsEl) {
      resultsEl.style.opacity = '0.4';
      resultsEl.style.transition = 'opacity 0.2s ease';
    }
    if (toolbarEl) {
      toolbarEl.style.opacity = '0.5';
      toolbarEl.style.transition = 'opacity 0.2s ease';
    }

    try {
      const response = await fetch(targetUrl);
      if (!response.ok) {
        window.location.href = targetUrl;
        return;
      }

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');

      // 1. Update Product Grid, In-Grid Filters & Top Active Pills
      const newResults = doc.getElementById('filter-results') || doc.querySelector('.main-products-grid__results');
      if (resultsEl && newResults) {
        resultsEl.innerHTML = newResults.innerHTML;
      }

      // 2. Update Collection Toolbar
      const newToolbar = doc.getElementById('products-toolbar');
      if (toolbarEl && newToolbar) {
        toolbarEl.innerHTML = newToolbar.innerHTML;
        if (typeof toolbarEl.bindTramEvents === 'function') {
          toolbarEl.bindTramEvents();
        }
      }

      // 3. Update Drawer Filters & Counts
      const newFacets = doc.getElementById('facets');
      const currentFacets = document.getElementById('facets');
      if (newFacets && currentFacets) {
        currentFacets.innerHTML = newFacets.innerHTML;
      }

      const newDrawerCount = doc.querySelector('.tram-drawer-products-count');
      const currentDrawerCount = document.querySelector('.tram-drawer-products-count');
      if (newDrawerCount && currentDrawerCount) {
        currentDrawerCount.innerHTML = newDrawerCount.innerHTML;
      }

      const newCloseBtn = doc.querySelector('.js-close-drawer-mob');
      const currentCloseBtn = document.querySelector('.js-close-drawer-mob');
      if (newCloseBtn && currentCloseBtn) {
        currentCloseBtn.innerHTML = newCloseBtn.innerHTML;
      }

      // 4. Update Piece Guide Section if present on the page
      const currentPieceGuides = document.querySelectorAll('.cpg-section');
      const newPieceGuides = doc.querySelectorAll('.cpg-section');
      if (currentPieceGuides.length > 0 && newPieceGuides.length > 0) {
        currentPieceGuides.forEach((cpg, idx) => {
          if (newPieceGuides[idx]) {
            cpg.innerHTML = newPieceGuides[idx].innerHTML;
          }
        });
        if (typeof window.initCollectionPieceGuide === 'function') {
          window.initCollectionPieceGuide();
        }
      }

      // 5. Update Address Bar URL
      window.history.pushState({ path: targetUrl }, '', targetUrl);

      // 6. Update sticky positions
      setupStickyActivePills();

      // 7. Reinitialize lazy images & custom pagination
      if (typeof initLazyImages === 'function') initLazyImages();
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination && typeof customPagination.reload === 'function') {
        customPagination.reload();
      }

      // 8. Dispatch theme filter update event
      document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });

      // Smooth scroll to top of collection products so user always sees the products starting from the top
      const scrollAnchor = document.getElementById('filter-results') || 
                           document.getElementById('collection-products-grid') || 
                           document.querySelector('.main-products-grid__results');
      if (scrollAnchor) {
        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
        const anchorRect = scrollAnchor.getBoundingClientRect();
        const anchorDocTop = anchorRect.top + currentScroll;

        let headerOffset = 0;
        if (typeof getStickyHeaderBottom === 'function') {
          headerOffset = getStickyHeaderBottom();
        }
        if (!headerOffset && document.querySelector('store-header[data-is-sticky="true"]')) {
          headerOffset = document.querySelector('store-header').offsetHeight || 60;
        }

        let tbOffset = 0;
        if (toolbarEl) {
          const isMobile = window.innerWidth < 900;
          const isTbSticky = isMobile
            ? (toolbarEl.classList.contains('is-sticky-mobile') || toolbarEl.dataset.stickMobile === 'true')
            : (toolbarEl.classList.contains('is-sticky-desktop') || toolbarEl.dataset.stickDesktop === 'true');
          if (isTbSticky) {
            tbOffset = toolbarEl.offsetHeight || 50;
          }
        }

        const targetScrollY = Math.max(0, Math.round(anchorDocTop - tbOffset - headerOffset));
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
      }
    } catch (err) {
      console.warn('[Tramontina] Tag AJAX navigation error:', err);
    } finally {
      if (resultsEl) resultsEl.style.opacity = '1';
      if (toolbarEl) toolbarEl.style.opacity = '1';
    }
  }

  // Expose global AJAX navigation function
  window.tramNavigateToTagUrl = navigateToTagUrl;

  function init() {
    bindGlobalFilterDelegation();
    setupStickyActivePills();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync sticky offsets on facet filter updates
  document.addEventListener('on:facet-filters:updated', function() {
    setupStickyActivePills();
  });

  // Handle browser back/forward buttons
  window.addEventListener('popstate', function() {
    window.location.reload();
  });
})();
